import { useCallback, useEffect, useRef } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { getTenantBootstrap, selectTenant } from '../lib/tenant';
import { clearPrivateAccountQueries, getPrivateQueryGeneration } from '../lib/queryPrivacy';
import type { TenantBootstrapResponse, TenantMembershipSummary, TenantSelectPayload } from '../types';

export const tenantKeys = {
  all: ['tenant'] as const,
  bootstrap: () => [...tenantKeys.all, 'bootstrap'] as const,
};

export function useTenantBootstrap(enabled: boolean = true) {
  const queryClient = useQueryClient();
  const skippedAutoSelectRef = useRef(false);
  const attemptedAutoSelectRef = useRef(false);

  const query = useQuery<TenantBootstrapResponse>({
    queryKey: tenantKeys.bootstrap(),
    queryFn: getTenantBootstrap,
    staleTime: 5 * 60 * 1000,
    retry: false,
    enabled,
  });

  const selectTenantMutation = useMutation({
    mutationFn: async (payload: TenantSelectPayload) => {
      const generation = getPrivateQueryGeneration();
      const data = await selectTenant(payload);
      return { data, generation };
    },
    onSuccess: async ({ data, generation }) => {
      if (generation !== getPrivateQueryGeneration()) return;
      const clearedGeneration = await clearPrivateAccountQueries(queryClient);
      if (clearedGeneration !== getPrivateQueryGeneration()) return;
      queryClient.setQueryData(tenantKeys.bootstrap(), data);
    },
  });
  const {
    isPending: isSwitchingTenant,
    mutate: selectTenantMutate,
    mutateAsync: selectTenantMutateAsync,
  } = selectTenantMutation;

  useEffect(() => {
    if (!enabled || skippedAutoSelectRef.current || attemptedAutoSelectRef.current) {
      return;
    }
    const familyMembership = selectSingleFamilyTenantMembership(query.data ?? null);
    const tenantId = familyMembership?.tenant?.id;
    if (!tenantId) {
      return;
    }
    attemptedAutoSelectRef.current = true;
    selectTenantMutate({ tenantId });
  }, [enabled, query.data, selectTenantMutate]);

  const selectTenantWithDefaultGuard = useCallback(async (payload: TenantSelectPayload): Promise<TenantBootstrapResponse> => {
    skippedAutoSelectRef.current = payload.communityMode === true;
    return (await selectTenantMutateAsync(payload)).data;
  }, [selectTenantMutateAsync]);

  return {
    bootstrap: query.data ?? null,
    memberships: query.data?.memberships ?? [],
    activeTenant: query.data?.activeTenant ?? null,
    isLoading: query.isLoading,
    isError: query.isError,
    error: query.error,
    refetch: query.refetch,
    selectTenant: selectTenantWithDefaultGuard,
    isSwitchingTenant,
  };
}

function selectSingleFamilyTenantMembership(bootstrap?: TenantBootstrapResponse | null): TenantMembershipSummary | null {
  if (!bootstrap || bootstrap.activeTenant?.tenant?.id) {
    return null;
  }

  const familyMemberships = (bootstrap.memberships ?? []).filter((membership) =>
    Boolean(membership.tenant?.id)
      && (membership.membershipStatus || '').trim().toUpperCase() === 'ACTIVE'
      && isFamilyRole(membership.roleCode)
  );

  return familyMemberships.length === 1 ? familyMemberships[0] : null;
}

function isFamilyRole(roleCode?: string | null): boolean {
  return new Set(['GUARDIAN', 'PARENT', 'FAMILY', 'ATHLETE', 'STUDENT'])
    .has((roleCode || '').trim().toUpperCase());
}
